import {skipToken, SkipToken} from '@reduxjs/toolkit/query';
import {match} from 'ts-pattern';

import {useMemo} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useGetStatisticsV2Query} from '../../../store/priceReportApi';
import {GetStatisticsV2ApiArg, StatisticsV2RequestBody} from '../../../types/api/api';
import {PriceReportType} from '../../../types/PriceReportType';
import {applyExactTotals, computeMarketMetrics, MarketMetrics} from '../utils/computeMarketMetrics';

/** Backend pagination cap: 'offset' + 'size' of statistics_v2 must stay below this. */
const MAX_PAGEABLE_CARS = 30_000;

export type UseMarketMetricsResult = {
  metrics: MarketMetrics | null;
  isLoading: boolean;
  isError: boolean;
};

/** Probe template: advertised cars only, count-sized page. */
function advertisedCountBody(body: StatisticsV2RequestBody): StatisticsV2RequestBody {
  return {...body, hideSoldIn30: true, hideSoldIn90: true, size: 1, offset: 0};
}

/**
 * Market metrics for one statistics_v2 request. The main query returns at most 1000
 * (cheapest) cars; when the filter matches more, tiny follow-up probes (size 1) fetch
 * the exact figures the cheap page cannot provide:
 *  - advertised-only probe → exact in-advertisement count,
 *  - sold-only probe → exact sold count,
 *  - cheaper-than-my-price probe → exact market position when my price ranks beyond
 *    the returned page,
 *  - max-price probe at the last advertised offset → the true maximum price; on
 *    markets beyond the pagination cap it pages inside the above-my-price subset.
 * Metrics refine progressively as the probes land; min and page-end prices are
 * always exact since they only need the cheapest cars.
 */
export function useMarketMetrics(
  queryArg: GetStatisticsV2ApiArg | SkipToken,
  priceReport: PriceReportType
): UseMarketMetricsResult {
  const {data, isFetching, isError} = useGetStatisticsV2Query(queryArg);

  const isSkipped = queryArg === skipToken;
  const isTruncated =
    !isSkipped && isNotNil(data) && (data.total_hits ?? 0) > (data.cars?.length ?? 0);
  const queryPrice = isSkipped ? null : (queryArg.body.query_price ?? null);
  const priceFilter = isSkipped ? null : (queryArg.body.price ?? null);

  const advertisedProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isTruncated
        ? {reportId: queryArg.reportId, body: advertisedCountBody(queryArg.body)}
        : skipToken,
    [isSkipped, isTruncated, queryArg]
  );
  const {data: advertisedProbe} = useGetStatisticsV2Query(advertisedProbeArg);
  const advertisedTotal = advertisedProbe?.total_hits ?? null;

  const soldProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isTruncated
        ? {
            reportId: queryArg.reportId,
            body: {...queryArg.body, hideStockCars: true, size: 1, offset: 0},
          }
        : skipToken,
    [isSkipped, isTruncated, queryArg]
  );
  const {data: soldProbe} = useGetStatisticsV2Query(soldProbeArg);
  const soldTotal = soldProbe?.total_hits ?? null;

  // My rank is derivable from the returned (cheapest) page only while some returned
  // advertised car is priced at or above my price. Once every one of them is cheaper,
  // the true rank lies beyond the page and a count-of-cheaper-cars probe pins it
  // exactly. An empty advertised page means no rank exists at all — not a probe case.
  const returnedAdvertised = (data?.cars ?? []).filter((car) => car.is_available && !car.is_sold);
  const isRankBeyondPage =
    isTruncated &&
    isNotNil(queryPrice) &&
    returnedAdvertised.length > 0 &&
    returnedAdvertised.every((car) => isNil(car.price_with_vat) || car.price_with_vat < queryPrice);
  // A price filter starting above my price means nothing can be cheaper — place 1.
  const cheaperUpperBound = isNotNil(queryPrice) ? queryPrice - 1 : null;
  const isCheaperCountTrivial =
    isNotNil(priceFilter?.gte) &&
    isNotNil(cheaperUpperBound) &&
    priceFilter.gte > cheaperUpperBound;

  const cheaperProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isRankBeyondPage && isNotNil(cheaperUpperBound) && !isCheaperCountTrivial
        ? {
            reportId: queryArg.reportId,
            body: {
              ...advertisedCountBody(queryArg.body),
              price: {
                gte: priceFilter?.gte ?? null,
                lte: isNotNil(priceFilter?.lte)
                  ? Math.min(priceFilter.lte, cheaperUpperBound)
                  : cheaperUpperBound,
              },
            },
          }
        : skipToken,
    [isSkipped, isRankBeyondPage, cheaperUpperBound, isCheaperCountTrivial, priceFilter, queryArg]
  );
  const {data: cheaperProbe} = useGetStatisticsV2Query(cheaperProbeArg);
  const advertisedCheaperCount = match({isRankBeyondPage, isCheaperCountTrivial})
    .with({isRankBeyondPage: false}, () => null)
    .with({isCheaperCountTrivial: true}, () => 0)
    .otherwise(() => cheaperProbe?.total_hits ?? null);

  // True maximum price. Within the pagination cap a single probe at the last offset
  // suffices; beyond it the probe pages inside the (smaller) above-my-price subset.
  const isMaxWithinCap =
    isNotNil(advertisedTotal) && advertisedTotal > 0 && advertisedTotal < MAX_PAGEABLE_CARS;
  const isMaxBeyondCap = isNotNil(advertisedTotal) && advertisedTotal >= MAX_PAGEABLE_CARS;

  // Strictly above my price — an inclusive bound would count a same-priced cohort,
  // which alone can exceed the pagination cap and block the probe.
  const abovePriceFilter = useMemo(
    () =>
      isNotNil(queryPrice)
        ? {
            gte: isNotNil(priceFilter?.gte)
              ? Math.max(priceFilter.gte, queryPrice + 1)
              : queryPrice + 1,
            lte: priceFilter?.lte ?? null,
          }
        : null,
    [queryPrice, priceFilter]
  );

  const aboveCountProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isTruncated && isMaxBeyondCap && isNotNil(abovePriceFilter)
        ? {
            reportId: queryArg.reportId,
            body: {...advertisedCountBody(queryArg.body), price: abovePriceFilter},
          }
        : skipToken,
    [isSkipped, isTruncated, isMaxBeyondCap, abovePriceFilter, queryArg]
  );
  const {data: aboveCountProbe} = useGetStatisticsV2Query(aboveCountProbeArg);
  const aboveTotal = aboveCountProbe?.total_hits ?? null;

  // Nothing above my price on a beyond-cap market: the maximum is exactly my price
  // if any advertised car sells at it — one count probe on the price-ties cohort.
  const isEqualsProbeRelevant =
    isMaxBeyondCap &&
    aboveTotal === 0 &&
    isNotNil(queryPrice) &&
    (isNil(priceFilter?.gte) || priceFilter.gte <= queryPrice);
  const equalsProbeArg = useMemo(
    (): GetStatisticsV2ApiArg | SkipToken =>
      !isSkipped && isTruncated && isEqualsProbeRelevant && isNotNil(queryPrice)
        ? {
            reportId: queryArg.reportId,
            body: {
              ...advertisedCountBody(queryArg.body),
              price: {gte: queryPrice, lte: queryPrice},
            },
          }
        : skipToken,
    [isSkipped, isTruncated, isEqualsProbeRelevant, queryPrice, queryArg]
  );
  const {data: equalsProbe} = useGetStatisticsV2Query(equalsProbeArg);
  const equalsPriceMax =
    isEqualsProbeRelevant && (equalsProbe?.total_hits ?? 0) > 0 ? queryPrice : null;

  const maxPriceProbeArg = useMemo((): GetStatisticsV2ApiArg | SkipToken => {
    if (isSkipped || !isTruncated) {
      return skipToken;
    }
    if (isMaxWithinCap && isNotNil(advertisedTotal)) {
      return {
        reportId: queryArg.reportId,
        body: {...advertisedCountBody(queryArg.body), offset: advertisedTotal - 1},
      };
    }
    if (
      isMaxBeyondCap &&
      isNotNil(abovePriceFilter) &&
      isNotNil(aboveTotal) &&
      aboveTotal > 0 &&
      aboveTotal < MAX_PAGEABLE_CARS
    ) {
      return {
        reportId: queryArg.reportId,
        body: {
          ...advertisedCountBody(queryArg.body),
          price: abovePriceFilter,
          offset: aboveTotal - 1,
        },
      };
    }
    return skipToken;
  }, [
    isSkipped,
    isTruncated,
    isMaxWithinCap,
    isMaxBeyondCap,
    advertisedTotal,
    abovePriceFilter,
    aboveTotal,
    queryArg,
  ]);
  const {data: maxPriceProbe} = useGetStatisticsV2Query(maxPriceProbeArg);
  const advertisedMaxPrice = maxPriceProbe?.cars?.[0]?.price_with_vat ?? equalsPriceMax ?? null;

  const metrics = useMemo(() => {
    if (isSkipped || !isNotNil(data) || isError) {
      return null;
    }
    const base = computeMarketMetrics(data, priceReport);
    if (!isTruncated) {
      return base;
    }
    return applyExactTotals(base, {
      advertisedCount: advertisedTotal,
      soldCount: soldTotal,
      advertisedMaxPrice,
      advertisedCheaperCount,
    });
  }, [
    isSkipped,
    data,
    isError,
    priceReport,
    isTruncated,
    advertisedTotal,
    soldTotal,
    advertisedMaxPrice,
    advertisedCheaperCount,
  ]);

  return {metrics, isLoading: isFetching, isError};
}
